<template>
  <div id="wt-text-styles-container" class="js-text-styles-container">
    <div v-if="styles.length" class="wt-text-styles-title">
      <small v-text="editingText.title"></small>
      <button
        v-if="styles.length"
        class="btn btn-link"
        @click="toggleEdit"
        v-text="editingText.button"
      />
    </div>

    <div class="wt-text-style-list">
      <text-style
        v-for="(s, index) in styles"
        :style-object="s"
        :editing="editing"
        :index="index"
        :key="s.id"
        :expanded="s.id === activeStyle"
        :programmer="programmer"
        @expanded="styleExpanded"
        @apply-style="styleApplied"
        @collapse="activeStyle = 0"
      />
    </div>

    <div
      v-if="!saveOpen && !editing"
      class="wt-save-text-style"
      :class="{ disabled: saveDisabled }"
      @click="openSaveBox"
    >
      Save as new text style
    </div>

    <template v-if="!editing && saveOpen">
      <div class="wt-save-text-style" :class="{ expanded: saveOpen }">
        <span class="wticon wticon-private" />
        <span class="wt-save-text-style-checkbox">
          <input id="chkStyleShared" type="checkbox" v-model="shareStyle" />
          <label for="chkStyleShared" />
        </span>
        <span class="wticon wticon-shared" />
        <input
          type="text"
          name="style-name"
          ref="input"
          v-model="name"
          placeholder="Text style name"
          :class="{ error }"
          @keypress.enter="saveStyle"
          @focus="error = false"
        />
      </div>
      <div class="save-button">
        <div class="wt-checkbox-sm">
          <div></div>
          <label>
            <input type="checkbox" v-model="saveBoxDesign" />
            <span>Box design</span>
          </label>
        </div>
        <div class="wt-checkbox-sm">
          <div></div>
          <label>
            <input type="checkbox" v-model="savePosition" />
            <span>Position</span>
          </label>
        </div>
        <button class="btn btn-link" @click="saveStyle">Save</button>
      </div>
    </template>
  </div>
</template>

<script>
/** (GW)
 * Asset Editor's text overlay stage Vue (stage bar)
 */
import TextStyle from 'AssetEditor/functionality/TextOverlay/TextStyle.vue';
import bus from 'v/AssetEditor/AssetEditorEventBus';
import { mapGetters } from 'vuex';

const initialData = {
  editing: false,
  saveOpen: false,
  saveDisabled: true,
  saveBoxDesign: true,
  savePosition: false,
  error: false,
  shareStyle: false,
  name: '',
  activeStyle: 0,
};

export default {
  components: {
    'text-style': TextStyle,
  },

  props: ['initialStyles', 'programmer'],

  data: function () {
    return Object.assign({}, initialData);
  },

  methods: {
    reset: function () {
      this.editing = false;
      this.saveOpen = false;
      this.saveBoxDesign = true;
      this.savePosition = false;
      this.shareStyle = false;
      this.name = '';
      this.activeStyle = 0;
    },

    styleExpanded: function (id) {
      if (id === this.activeStyle) {
        this.activeStyle = 0;
        return;
      }
      this.saveOpen = false;
      this.activeStyle = id;
    },

    styleApplied: function (id, subStyle) {
      const style = _.find(this.styles, { id });
      if (style) {
        bus.$emit('apply-style', style, subStyle);
      }
    },

    saveStyle: function () {
      if (!this.name) {
        this.error = true;
        return;
      }
      bus.$emit('save-style', {
        name: this.name,
        saveBoxDesign: this.saveBoxDesign,
        savePosition: this.savePosition,
        shareStyle: this.shareStyle,
      });
      this.name = initialData.name;
      this.saveBoxDesign = initialData.saveBoxDesign;
      this.savePosition = initialData.savePosition;

      this.saveOpen = false;
    },

    toggleEdit: function () {
      this.activeStyle = 0;
      this.editing = !this.editing;
    },

    openSaveBox: function () {
      if (this.saveDisabled) {
        return;
      }
      this.saveOpen = true;
      this.activeStyle = 0;
      this.$nextTick(() => {
        this.$refs.input.focus();
      });
    },

    scrollToView: function () {
      if (this.activeStyle) {
        const child = _.find(this.$children, (child) => {
          return child.styleObject.id === this.activeStyle;
        });
        if (child) {
          child.scrollToView();
        }
      }
    },

    setActiveStyle: function (id) {
      this.activeStyle = id;
    },

    setSaveDisabled: function (isDisabled) {
      this.saveDisabled = isDisabled;
    },
  },

  created: function () {
    bus.$on('to:isEmpty', this.setSaveDisabled);
    bus.$on('active-style', this.setActiveStyle);
    bus.$on('reset-text-styles-box', this.reset);
  },

  beforeDestroy: function () {
    bus.$off('to:isEmpty', this.setSaveDisabled);
    bus.$off('active-style', this.setActiveStyle);
    bus.$off('reset-text-styles-box', this.reset);
  },

  computed: {
    ...mapGetters('assetEditor', ['story']),

    editingText: function () {
      return this.editing
        ? { button: 'Back', title: 'Text Styles' }
        : { button: 'Manage', title: 'Text Styles' };
    },
    styles: function () {
      return this.story.textStyles;
    },
    numStyles: function () {
      return this.styles.length;
    },
  },

  watch: {
    numStyles: function (numStyle) {
      if (!numStyle) {
        this.editing = false;
      }
    },
  },
};
</script>
